<div class="report-content">

	<div class="row mb-4">
		<div class="col-12">
			<ms-report-date-filter
				mode="asOf"
				[(asOfDate)]="asOfDate"
				[disabled]="isLoading()"
				(applied)="onDateChanged()" />
		</div>
	</div>

	@if (errorMessage()) {
		<div class="alert alert-danger" role="alert">
			<fa-duotone-icon icon="triangle-exclamation" class="me-2" />
			{{ errorMessage() }}
		</div>
	}

	@if (isLoading() && !report()) {
		<div class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>
	}

	@if (report(); as data) {
		<div class="row g-3">
			<!-- Main balance sheet card -->
			<div class="col-lg-8">
				<div class="bs-main-card p-4">
					<h5 class="bs-main-title mb-4">GAAP Balance Sheet</h5>

					<!-- ASSETS -->
					<div class="bs-section-bar" data-accent="success">
						<fa-duotone-icon icon="wallet" secondaryOpacity="0.7" class="me-2 bs-section-icon" />
						Assets
					</div>
					<table class="table table-sm bs-table mb-0">
						<tbody>
							@if (data.assets.accounts && data.assets.accounts.length > 0) {
								@for (row of data.assets.accounts; track row.account.chartOfAccountId) {
									<tr>
										<td class="bs-col-account">{{ row.account.accountNumber }}</td>
										<td class="bs-col-description">
											{{ row.account.name }}
											@if (!row.account.isActive) {
												<span class="badge bg-warning-subtle text-warning-emphasis ms-1">Retired</span>
											}
										</td>
										<td class="bs-col-amount text-end">{{ formatAmount(row.amount) }}</td>
									</tr>
								}
							} @else {
								<tr>
									<td colspan="3" class="text-center text-muted py-3">No asset activity.</td>
								</tr>
							}
							<tr class="bs-total-row">
								<td class="bs-col-account"></td>
								<td class="bs-col-description fw-bold">Total Assets</td>
								<td class="bs-col-amount text-end fw-bold">{{ formatAmount(data.assets.total) }}</td>
							</tr>
						</tbody>
					</table>

					<!-- LIABILITIES -->
					<div class="bs-section-bar mt-4" data-accent="danger">
						<fa-duotone-icon icon="file-invoice-dollar" secondaryOpacity="0.7" class="me-2 bs-section-icon" />
						Liabilities
					</div>
					<table class="table table-sm bs-table mb-0">
						<tbody>
							@if (data.liabilities.accounts && data.liabilities.accounts.length > 0) {
								@for (row of data.liabilities.accounts; track row.account.chartOfAccountId) {
									<tr>
										<td class="bs-col-account">{{ row.account.accountNumber }}</td>
										<td class="bs-col-description">
											{{ row.account.name }}
											@if (!row.account.isActive) {
												<span class="badge bg-warning-subtle text-warning-emphasis ms-1">Retired</span>
											}
										</td>
										<td class="bs-col-amount text-end">{{ formatAmount(row.amount) }}</td>
									</tr>
								}
							} @else {
								<tr>
									<td colspan="3" class="text-center text-muted py-3">No liability activity.</td>
								</tr>
							}
							<tr class="bs-total-row">
								<td class="bs-col-account"></td>
								<td class="bs-col-description fw-bold">Total Liabilities</td>
								<td class="bs-col-amount text-end fw-bold">{{ formatAmount(data.liabilities.total) }}</td>
							</tr>
						</tbody>
					</table>

					<!-- EQUITY -->
					<div class="bs-section-bar mt-4" data-accent="primary">
						<fa-duotone-icon icon="piggy-bank" secondaryOpacity="0.7" class="me-2 bs-section-icon" />
						Equity
					</div>
					<table class="table table-sm bs-table mb-0">
						<tbody>
							@if (data.equity.accounts && data.equity.accounts.length > 0) {
								@for (row of data.equity.accounts; track row.account.chartOfAccountId) {
									<tr>
										<td class="bs-col-account">{{ row.account.accountNumber }}</td>
										<td class="bs-col-description">
											{{ row.account.name }}
											@if (!row.account.isActive) {
												<span class="badge bg-warning-subtle text-warning-emphasis ms-1">Retired</span>
											}
										</td>
										<td class="bs-col-amount text-end">{{ formatAmount(row.amount) }}</td>
									</tr>
								}
							}
							<tr>
								<td class="bs-col-account"></td>
								<td class="bs-col-description fst-italic">Current Year Net Income</td>
								<td class="bs-col-amount text-end">{{ formatAmount(data.equity.currentYearNetIncome) }}</td>
							</tr>
							<tr class="bs-total-row">
								<td class="bs-col-account"></td>
								<td class="bs-col-description fw-bold">Total Equity</td>
								<td class="bs-col-amount text-end fw-bold">{{ formatAmount(data.equity.total) }}</td>
							</tr>
						</tbody>
					</table>

					<!-- Grand total -->
					<div class="bs-grand-total d-flex justify-content-between align-items-center mt-4">
						<span class="fw-bold">
							<fa-duotone-icon icon="calculator" secondaryOpacity="0.7" class="me-2 bs-grand-total-icon" />
							Total Liabilities &amp; Equity
						</span>
						<span class="fw-bold">{{ formatAmount(data.totalLiabilitiesAndEquity) }}</span>
					</div>

					@if (!isBalanced()) {
						<div class="alert alert-warning mt-3 mb-0" role="alert">
							<fa-duotone-icon icon="triangle-exclamation" class="me-2" />
							Total Assets do not match Total Liabilities &amp; Equity.
						</div>
					}
				</div>
			</div>

			<!-- Sidebar: accounting identity + key ratios -->
			<div class="col-lg-4">
				<div class="bs-sidebar-card p-3 mb-3">
					<h6 class="bs-sidebar-title mb-3">Accounting Identity</h6>

					@if (identityChart(); as chart) {
						<svg class="bs-identity-chart" viewBox="0 0 120 110" role="img"
							aria-label="Accounting identity: Assets equals Liabilities plus Equity">
							<!-- Background tracks (show the zero state for each column) -->
							<rect class="bs-identity-track" x="15" y="5" width="35" height="95" rx="2" />
							<rect class="bs-identity-track" x="70" y="5" width="35" height="95" rx="2" />

							<!-- Left column: Assets (anchored bottom) -->
							<rect class="bs-identity-assets"
								x="15"
								[attr.y]="100 - chart.assetsHeightPct * 0.95"
								width="35"
								[attr.height]="chart.assetsHeightPct * 0.95" rx="2" />

							<!-- Right column: Liabilities (bottom) + Equity stacked above, both anchored bottom -->
							<rect class="bs-identity-liabilities"
								x="70"
								[attr.y]="100 - chart.liabilitiesHeightPct * 0.95"
								width="35"
								[attr.height]="chart.liabilitiesHeightPct * 0.95" rx="2" />
							<rect class="bs-identity-equity"
								x="70"
								[attr.y]="100 - (chart.liabilitiesHeightPct + chart.equityHeightPct) * 0.95"
								width="35"
								[attr.height]="chart.equityHeightPct * 0.95" rx="2" />
						</svg>

						<div class="bs-identity-labels small mb-3">
							<div class="bs-identity-col-label">
								<div class="fw-bold">Assets</div>
								<div class="text-muted">{{ formatAmount(data.assets.total) }}</div>
							</div>
							<div class="bs-identity-col-label">
								<div class="fw-bold">Liabilities + Equity</div>
								<div class="text-muted">{{ formatAmount(data.totalLiabilitiesAndEquity) }}</div>
							</div>
						</div>

						<ul class="bs-identity-legend list-unstyled mb-0">
							<li class="d-flex align-items-center justify-content-between py-1">
								<span class="d-flex align-items-center">
									<span class="bs-identity-swatch bs-identity-swatch-equity me-2"></span>
									Equity
								</span>
								<span class="text-muted">{{ chart.equitySharePct | number:'1.0-0' }}%</span>
							</li>
							<li class="d-flex align-items-center justify-content-between py-1">
								<span class="d-flex align-items-center">
									<span class="bs-identity-swatch bs-identity-swatch-liabilities me-2"></span>
									Liabilities
								</span>
								<span class="text-muted">{{ chart.liabilitiesSharePct | number:'1.0-0' }}%</span>
							</li>
						</ul>
					} @else {
						<div class="text-center text-muted py-3">No data to visualize.</div>
					}
				</div>

				@if (keyRatios().length > 0) {
					<div class="bs-sidebar-card p-3">
						<h6 class="bs-sidebar-title mb-3">Key Ratios</h6>

						@for (ratio of keyRatios(); track ratio.label) {
							<div class="bs-ratio-card p-3 mb-2">
								<div class="d-flex justify-content-between align-items-start">
									<span class="bs-ratio-label">{{ ratio.label }}</span>
									<fa-duotone-icon
										[icon]="ratio.trendUp ? 'arrow-trend-up' : 'arrow-trend-down'"
										[class.text-success]="ratio.trendUp"
										[class.text-danger]="!ratio.trendUp" />
								</div>
								<div class="bs-ratio-value">{{ ratio.value }}</div>
							</div>
						}
					</div>
				}
			</div>
		</div>
	}

</div>
